// ─── Auto-Balance Layout ──────────────────────────────────────
// Measures weighted visual center of collage elements and nudges
// them toward a target center. Respects intent constraints:
//   - fixed/high-priority items move less
//   - decorative items move more
//   - all values clamped to viewport

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getPosition(element, device) {
  if (device === 'mobile') {
    return element.layout.mobile ?? element.layout.desktop;
  }
  return element.layout.desktop;
}

function getImportanceWeight(importance) {
  switch (importance) {
    case 'high': return 1.35;
    case 'medium': return 1.0;
    case 'low':
    default: return 0.7;
  }
}

function getRoleWeight(role) {
  switch (role) {
    case 'primaryHeadline': return 1.15;
    case 'image': return 1.25;
    case 'bodyText': return 1.0;
    case 'secondaryHeadline': return 0.95;
    case 'cta': return 0.95;
    case 'metaText': return 0.65;
    case 'decorative': return 0.45;
    default: return 1.0;
  }
}

function getVisualWeight(element, device) {
  const pos = getPosition(element, device);
  const widthWeight = clamp(pos.w, 0.08, 1);
  const importanceWeight = getImportanceWeight(element.intent?.importance);
  const roleWeight = getRoleWeight(element.intent?.role);
  return widthWeight * importanceWeight * roleWeight;
}

function getMobilityFactor(element) {
  const behavior = element.intent?.behavior;
  const importance = element.intent?.importance;
  const role = element.intent?.role;

  if (role === 'primaryHeadline') return 0.35;
  if (behavior === 'fixed' && importance === 'high') return 0.45;
  if (behavior === 'fixed') return 0.6;
  if (behavior === 'decorative') return 1.0;
  if (role === 'decorative') return 1.0;
  if (importance === 'low') return 0.9;
  return 0.75;
}

function isEligibleForNudge(element, device) {
  const pos = getPosition(element, device);
  if (pos.w <= 0) return false;
  if (device === 'mobile' && element.intent?.mobileBehavior === 'hide') return false;
  return true;
}

/**
 * Calculate the weighted visual center of a set of elements.
 * Good targets: 0.48–0.52 desktop, 0.44–0.48 mobile
 */
export function calculateVisualCenter(elements, options = {}) {
  const device = options.device || 'desktop';

  let weightedX = 0;
  let totalWeight = 0;

  for (const element of elements) {
    if (!isEligibleForNudge(element, device)) continue;
    const pos = getPosition(element, device);
    const weight = getVisualWeight(element, device);
    weightedX += pos.x * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return 0.5;
  return weightedX / totalWeight;
}

const DEFAULT_OPTIONS = {
  device: 'desktop',
  targetCenter: 0.5,
  deadZone: 0.015,
  maxShift: 0.06,
  strength: 0.65,
};

/**
 * Auto-balance a set of collage elements toward a target visual center.
 * Returns a new array with adjusted x positions.
 */
export function autoBalanceLayout(elements, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const currentCenter = calculateVisualCenter(elements, opts);
  const drift = currentCenter - opts.targetCenter;

  if (Math.abs(drift) <= opts.deadZone) {
    return elements;
  }

  const globalCorrection = clamp(
    -drift * opts.strength,
    -opts.maxShift,
    opts.maxShift
  );

  return elements.map((element) => {
    if (!isEligibleForNudge(element, opts.device)) return element;

    const currentPos = getPosition(element, opts.device);
    const mobility = getMobilityFactor(element);

    // Elements farther from target get slightly more correction
    const distanceFromTarget = currentPos.x - opts.targetCenter;
    const distanceFactor = 1 + Math.min(Math.abs(distanceFromTarget) * 0.6, 0.25);

    const shift = globalCorrection * mobility * distanceFactor;
    const nextX = clamp(currentPos.x + shift, currentPos.w / 2, 1 - currentPos.w / 2);

    if (opts.device === 'mobile') {
      return {
        ...element,
        layout: {
          ...element.layout,
          mobile: {
            ...(element.layout.mobile ?? element.layout.desktop),
            x: nextX,
          },
        },
      };
    }

    return {
      ...element,
      layout: {
        ...element.layout,
        desktop: {
          ...element.layout.desktop,
          x: nextX,
        },
      },
    };
  });
}
